import type { SupabaseClient } from '@supabase/supabase-js'
import {
  detectionProfileSchema,
  detectionProfilesResultSchema,
  missionDetailSchema,
  missionHeaderResultSchema,
  missionListResultSchema,
  missionsDashboardSchema,
  missionTrackSchema,
} from '@groupe-reca/contracts'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { FieldMissionRepository } from '../../application/FieldMissionRepository'
import { MissionCommandError, missionErrorCode } from '../../domain/fieldMission'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

export function createSupabaseFieldMissionRepository(client: AppSupabaseClient): FieldMissionRepository {
  // Fonctions de la migration 20261022010000, absentes des types générés : réponses validées par les contrats.
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, args?: Record<string, unknown>): Promise<unknown> {
    const response = (await rpcClient.rpc(name, args)) as unknown as RpcResponse
    if (response.error) throw new MissionCommandError(missionErrorCode(response.error.message))
    return response.data
  }

  return {
    async dashboard() {
      return missionsDashboardSchema.parse(await call('missions_dashboard_v1'))
    },
    async list(filters) {
      return missionListResultSchema.parse(
        await call('missions_list_v1', {
          p_command: {
            tab: filters.tab,
            search: filters.search,
            status: filters.status,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            page: filters.page,
            pageSize: filters.pageSize,
          },
        }),
      )
    },
    async detail(missionId) {
      try {
        return missionDetailSchema.parse(await call('mission_detail_v1', { p_mission_id: missionId }))
      } catch (error) {
        if (error instanceof MissionCommandError && error.code === 'MISSION_NOT_FOUND') return null
        throw error
      }
    },
    async track(missionId) {
      return missionTrackSchema.parse(await call('mission_track_v1', { p_mission_id: missionId }))
    },
    async cancel(command) {
      missionHeaderResultSchema.parse(await call('cancel_mission_v1', { p_command: command }))
    },
    async correctHourMeter(command) {
      await call('record_hour_meter_correction_v1', { p_command: command })
    },
    async clearUnresolvedItem(command) {
      await call('clear_unresolved_mission_item_v1', { p_command: command })
    },
    async detectionProfiles() {
      return detectionProfilesResultSchema.parse(await call('mission_detection_profiles_v1'))
    },
    async publishDetectionProfile(command) {
      return detectionProfileSchema.parse(
        await call('set_mission_detection_profile_v1', { p_command: command }),
      )
    },
  }
}
